
import { Target, Clock, TrendingUp, AlertTriangle, CheckCircle, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";

const AmIOKScoreExplanation = () => {
  const [selectedRange, setSelectedRange] = useState<string | null>(null);

  const scoreRanges = [
    {
      range: "0-20",
      title: "Very Low Risk",
      description: "Self-care",
      details: "Symptoms are minimal with little impact on daily life. Home remedies, rest, and self-monitoring are typically sufficient.",
      action: "Continue normal activities",
      color: "green",
      icon: CheckCircle,
      bgClass: "bg-green-50 hover:bg-green-100",
      borderClass: "border-green-200 hover:border-green-300",
      textClass: "text-green-700"
    },
    {
      range: "20-40",
      title: "Minimal Concern", 
      description: "Monitor & routine care",
      details: "Mild symptoms that may benefit from routine medical consultation. Consider scheduling a non-urgent appointment.",
      action: "Schedule routine consultation",
      color: "blue",
      icon: Clock,
      bgClass: "bg-blue-50 hover:bg-blue-100",
      borderClass: "border-blue-200 hover:border-blue-300", 
      textClass: "text-blue-700"
    },
    {
      range: "40-60",
      title: "Moderate Risk",
      description: "Medical evaluation",
      details: "Symptoms affecting quality of life or indicating potential medical condition. Medical evaluation advisable within days.",
      action: "Book medical appointment",
      color: "yellow",
      icon: TrendingUp,
      bgClass: "bg-yellow-50 hover:bg-yellow-100",
      borderClass: "border-yellow-200 hover:border-yellow-300",
      textClass: "text-yellow-700"
    },
    {
      range: "60-80",
      title: "Elevated Risk",
      description: "Prompt consultation",
      details: "Concerning symptoms requiring medical assessment. Contact healthcare provider within 24-48 hours.",
      action: "Contact healthcare provider",
      color: "orange", 
      icon: AlertTriangle,
      bgClass: "bg-orange-50 hover:bg-orange-100",
      borderClass: "border-orange-200 hover:border-orange-300",
      textClass: "text-orange-700"
    },
    {
      range: "80-100",
      title: "High Priority",
      description: "Urgent attention",
      details: "Serious symptoms requiring immediate medical evaluation. Seek urgent care or emergency services.",
      action: "Seek immediate care",
      color: "red",
      icon: AlertTriangle,
      bgClass: "bg-red-50 hover:bg-red-100", 
      borderClass: "border-red-200 hover:border-red-300",
      textClass: "text-red-700"
    }
  ];

  return (
    <div className="mb-8">
      <Card className="p-6 bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
        <div className="text-center mb-6">
          <Target className="h-12 w-12 text-primary mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-foreground mb-2">AmIOK Health Score</h3>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Evidence-based scoring using clinical assessment tools and medical research
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {scoreRanges.map((range, index) => {
            const Icon = range.icon;
            const isSelected = selectedRange === range.range;
            
            return (
              <div key={index} className="relative">
                  <Button
                    variant="outline"
                    className={`w-full h-auto p-2 ${range.bgClass} ${range.borderClass} transition-all cursor-pointer ${
                      isSelected ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => setSelectedRange(isSelected ? null : range.range)}
                  >
                    <div className="text-center">
                      <div className={`text-lg font-bold ${range.textClass} mb-0.5`}>{range.range}</div>
                      <div className={`text-xs font-semibold ${range.textClass} mb-0.5`}>{range.title}</div>
                      <div className={`text-xs ${range.textClass} opacity-80 mb-1 leading-tight`}>{range.description}</div>
                      <Icon className={`h-3 w-3 ${range.textClass} mx-auto`} />
                    </div>
                  </Button>
                
                {isSelected && (
                  <Card className="absolute top-full left-0 right-0 mt-2 p-4 bg-background border shadow-lg z-10">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">{range.details}</p>
                      <div className="flex items-center gap-2 pt-2">
                        <Info className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium text-primary">{range.action}</span>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            Click on any score range to learn more about what it means for your health
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AmIOKScoreExplanation;
